import { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, Link, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import EntitySelectBox from '@components/EntitySelectBox'
import EntitySelector from '@components/EntitySelector'

import useCreateOrEditOrderLeadTimeModal from './hooks/useCreateOrEditOrderLeadTimeModal'

const confirmDelete = (deleteOrderLeadTime, deleteFn, closeModal) => {
  const deleteOrderLeadTimePayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Order Lead Time Deleted',
  }

  swal.fire({
    confirmButtonColor: '#DD6B55',
    confirmButtonText: 'Yes, delete it.',
    icon: 'warning',
    showCancelButton: true,
    text: 'Are you sure?',
    title: 'Delete Order Lead Time?',
  }).then(({ value }) => {
    if (value){
      deleteOrderLeadTime(deleteOrderLeadTimePayload)
    }
  })
}

const createOrUpdateOrderLeadTime = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createOrderLeadTime, updateOrderLeadTime },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const orderLeadTimePayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.orderLeadTime,
    toastText: `Order Lead Time ${text}`,
  }

  if (entityState.id) return updateOrderLeadTime(orderLeadTimePayload)

  return createOrderLeadTime(orderLeadTimePayload)
}

const CreateOrEditOrderLeadTimeModal = (props) => {
  const { callbacks, modalKey = 'CreateOrEditOrderLeadTimeModal', showModal } = props
  const { closeModal, deleteOrderLeadTime } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { orderLeadTime } = modalPayload

  const {
    callbacks: {
      createOrderLeadTime: createFn,
      deleteOrderLeadTime: deleteFn,
      hideProductSelector,
      setServiceId,
      showProductSelector,
      updateOrderLeadTime: updateFn,
    },
    creating,
    deleting,
    formPayload,
    serviceId,
    showProductSelect,
    updating,
  } = useCreateOrEditOrderLeadTimeModal(orderLeadTime)

  const {
    entityState,
    entityState: {
      id,
      orderable_id,
      orderable_type,
      unit,
      value,
    },
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  const isEditing = !!id
  const saveOrCreateButtonText = isEditing ? 'Save Changes' : 'Add Lead Time'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Lead Time" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Service">
            <EntitySelectBox
              disabled={isEditing}
              entityKey="services"
              onChange={e => setServiceId(e.target.value)}
              value={serviceId}
            />
          </Form.Field>

          {!showProductSelect && !isEditing && (
            <Button
              buttonStyle="secondaryUtility"
              marginTop="large"
              onClick={() => showProductSelector()}
              size="medium"
            >
              Select a Product
            </Button>
          )}

          {showProductSelect && (
            <EntitySelector
              callbacks={{
                selectItem: product => (
                  setEntityState({ orderable_id: product ? product.id : null, orderable_type: 'ServiceProduct' })
                ),
              }}
              disabled={isEditing}
              endpoint="service_products"
              entityKey="serviceProducts"
              label="Product"
              labelRight={!isEditing ? (
                <Link onClick={() => hideProductSelector()} textProps={{ fontSize: 'xsmall' }}>
                  Hide Product
                </Link>
              ) : null}
              marginTop="large"
              searchRequestOptions={{ service_id: serviceId }}
              selectedItemId={orderable_type === 'ServiceProduct' ? orderable_id : undefined}
            />
          )}

          <SectionDivider boxProps={{ marginY: 'large' }}>Time Period</SectionDivider>

          <Form.Field label="Number">
            <input data-value-type="integer" name="value" type="text" value={value} {...handlers} />
          </Form.Field>

          <Form.Field label="Units" marginTop="large">
            <select name="unit" value={unit} {...handlers}>
              <option value="days">days</option>
              <option value="weeks">weeks</option>
            </select>
          </Form.Field>

          {isEditing && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(deleteOrderLeadTime, deleteFn, closeModal)}
                size="medium"
              >
                Delete Order Lead Time
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateOrderLeadTime(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditOrderLeadTimeModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditOrderLeadTimeModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
